import { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { Timestamp } from 'firebase/firestore';

// POI types (matching municipality app structure)
export enum POIStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
  UNDER_REVIEW = 'under_review'
}

export enum POICategory {
  HISTORICAL_SITE = 'historical_site',
  MUSEUM = 'museum',
  THEATRE = 'theatre',
  CULTURAL_CENTER = 'cultural_center',
  LIBRARY = 'library',
  ART_GALLERY = 'art_gallery',
  HISTORICAL_SCHOOL = 'historical_school',
  EDUCATION_INSTITUTE = 'education_institute',
  PARK = 'park',
  SQUARE = 'square',
  BEACH = 'beach',
  VIEWPOINT = 'viewpoint',
  CYCLING_ROUTE = 'cycling_route',
  SPORTS_VENUE = 'sports_venue',
  SWIMMING_POOL = 'swimming_pool',
  SHOPPING_STREET = 'shopping_street',
  MARKET = 'market',
  ACCOMMODATION = 'accommodation',
  CAR_PARKING = 'car_parking',
  GOVERNMENT_BUILDING = 'government_building',
  PLACE_OF_WORSHIP = 'place_of_worship',
  RESTAURANT = 'restaurant',
  ENTERTAINMENT = 'entertainment',
  HEALTH = 'health',
  TRANSPORT = 'transport',
  ARTIST_WORKSPACE = 'artist_workspace',
  OTHER = 'other'
}

export interface POITranslation {
  title: string;
  description: string;
  address?: string;
}

export interface POI {
  id: string;
  municipalityId: string;
  status: POIStatus;
  category: POICategory;
  translations: Record<string, POITranslation>;
  address: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  authorId: string;
  authorName: string;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  viewCount: number;
  uniqueViewCount: number;
}

interface POIStatistics {
  total: number;
  published: number;
  draft: number;
  archived: number;
  underReview: number;
  totalViews: number;
  averageViews: number;
}

interface POIListResponse {
  items: POI[];
  total: number;
  statistics: POIStatistics;
}

// Helper to convert Firestore Timestamp to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
};

// Convert Firestore data to POI
const convertFirestoreData = (data: unknown, id: string, municipalityId: string): POI => {
  const docData = data as Record<string, unknown>;
  
  // Convert translations to proper format
  const translationsData = docData.translations as Record<string, { title?: string; description?: string; address?: string }> | undefined;
  const translations: Record<string, POITranslation> = {};
  if (translationsData) {
    Object.keys(translationsData).forEach((lang) => {
      const trans = translationsData[lang];
      if (trans) {
        translations[lang] = {
          title: trans.title || '',
          description: trans.description || '',
          address: trans.address,
        };
      }
    });
  }

  return {
    id,
    municipalityId,
    status: (docData.status as POIStatus) || POIStatus.DRAFT,
    category: (docData.category as POICategory) || POICategory.OTHER,
    translations,
    address: (docData.address as string) || '',
    coordinates: docData.coordinates as { lat: number; lng: number } | undefined,
    authorId: (docData.authorId as string) || '',
    authorName: (docData.authorName as string) || '',
    createdAt: toDate(docData.createdAt),
    updatedAt: toDate(docData.updatedAt),
    publishedAt: docData.publishedAt ? toDate(docData.publishedAt) : undefined,
    viewCount: (docData.viewCount as number) || 0,
    uniqueViewCount: (docData.uniqueViewCount as number) || 0,
  };
};

export const useMunicipalityPOIs = (municipalityId: string | null) => {
  const [data, setData] = useState<POIListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setLoading(false);
      setData(null);
      return;
    }

    const fetchPOIs = async () => {
      try {
        setLoading(true);
        setError(null);

        const poisCollection = collection(db, 'municipalities', municipalityId, 'poi');
        const snapshot = await getDocs(query(poisCollection, orderBy('createdAt', 'desc')));

        const items: POI[] = [];
        let totalViews = 0;
        let publishedCount = 0;
        let draftCount = 0;
        let archivedCount = 0;
        let underReviewCount = 0;

        snapshot.forEach((doc) => {
          const poiItem = convertFirestoreData(doc.data(), doc.id, municipalityId);
          items.push(poiItem);

          // Calculate statistics
          totalViews += poiItem.viewCount || 0;
          
          // Categorize POIs by status
          if (poiItem.status === POIStatus.PUBLISHED) {
            publishedCount++;
          } else if (poiItem.status === POIStatus.DRAFT) {
            draftCount++;
          } else if (poiItem.status === POIStatus.ARCHIVED) {
            archivedCount++;
          } else if (poiItem.status === POIStatus.UNDER_REVIEW) {
            underReviewCount++;
          }
        });

        const total = items.length;
        const averageViews = total > 0 ? Math.round(totalViews / total) : 0;

        setData({
          items,
          total,
          statistics: {
            total,
            published: publishedCount,
            draft: draftCount,
            archived: archivedCount,
            underReview: underReviewCount,
            totalViews,
            averageViews,
          },
        });
      } catch (err) {
        console.error('Error fetching municipality POIs:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch municipality POIs');
      } finally {
        setLoading(false);
      }
    };

    fetchPOIs();
  }, [municipalityId]);

  return { data, loading, error };
};
